import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';

/**
 * Trasferimento al checkout ospitato da Nexi.
 *
 * Il form si invia da solo in ISO-8859-1: il browser **deve** atterrare sul
 * dominio Nexi, perché è lì che avviene il 3D Secure e lì che i dati della
 * carta vengono inseriti. Dai nostri sistemi non passano mai.
 *
 * L'importo è mostrato prima di partire perché può essere **maggiore** del
 * totale del carrello: il gestionale aggiunge le spese di incasso della
 * condizione di pagamento, e quello che addebitiamo è il totale documento.
 */
export default function PaymentRedirectPage() {
  const { state } = useLocation() as {
    state?: { action: string; fields: Record<string, string> };
  };
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [inviato, setInviato] = useState(false);

  useEffect(() => {
    if (!state?.action) {
      navigate('/area-clienti/checkout', { replace: true });
      return;
    }
    // Una pausa breve: abbastanza per leggere l'importo, non tanto da
    // sembrare che la pagina si sia piantata.
    const timer = setTimeout(() => {
      setInviato(true);
      formRef.current?.submit();
    }, 1200);
    return () => clearTimeout(timer);
  }, [state, navigate]);

  if (!state?.action) return null;

  const importo = Number(state.fields.importo || 0) / 100;

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="payment-redirect-page">
      <div className="rounded-xl border bg-white p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="w-6 h-6 text-[hsl(214,58%,41%)]" />
        </div>

        <h1 className="text-lg font-semibold mb-2">Trasferimento al pagamento sicuro</h1>
        <p className="text-sm text-muted-foreground mb-5">
          Stai per essere portato sulla pagina protetta di Nexi. I dati della carta non
          transitano dai nostri sistemi.
        </p>

        <div className="rounded-lg border bg-[hsl(var(--dtk-canvas))] px-4 py-3 mb-5 text-left">
          <div className="text-[11px] text-muted-foreground mb-0.5">
            Importo che verrà addebitato (totale documento, IVA e spese incluse)
          </div>
          <div className="text-xl font-semibold tabular-nums" data-testid="payment-redirect-amount">
            € {importo.toFixed(2).replace('.', ',')}
          </div>
        </div>

        {/* I circuiti realmente accettati dal terminale. Il vecchio finto
            gateway mostrava anche AMEX, che non è mai stato abilitato:
            qui si dichiara solo ciò che funziona davvero. */}
        <div className="flex items-center justify-center gap-2 mb-5">
          <span className="inline-flex items-center justify-center h-8 w-14 rounded border bg-white">
            <img src="/payments/visa.png" alt="VISA" className="h-4 max-w-full object-contain" />
          </span>
          <span className="inline-flex items-center justify-center h-8 w-14 rounded border bg-white">
            <img src="/payments/mastercard.png" alt="Mastercard" className="h-5 max-w-full object-contain" />
          </span>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {inviato ? 'Apertura della pagina di pagamento…' : 'Preparazione…'}
        </div>

        <form ref={formRef} method="POST" action={state.action} acceptCharset="ISO-8859-1">
          {Object.entries(state.fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
          {/* Senza JavaScript il trasferimento non parte da solo: il pulsante
              è l'unica via, non un ornamento. */}
          <noscript>
            <button
              type="submit"
              className="mt-4 h-10 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium"
            >
              Vai al pagamento
            </button>
          </noscript>
        </form>
      </div>
    </div>
  );
}
